import React, { ReactElement } from "react";
import { ApolloError, gql, useMutation } from "@apollo/client";
import { normalizeErrors } from "../../../../utils/normalizeErrors";
import { NormalizedErrorMap } from "../../../../types/NormalizedErrorMap";
import { PhoneOtpRequestMutationMutation, PhoneOtpRequestMutationMutationVariables } from "../../../../schemaTypes";

const mapPhoneOtpRequestApolloError = (message: string): NormalizedErrorMap => {
  const normalizedMessage = message.toLowerCase();

  if (normalizedMessage.includes("phone")) {
    return { phone: message };
  }

  return { general: message };
};

const phoneOtpRequestMutation = gql`
  mutation PhoneOtpRequestMutation(
    $input: PhoneOtpRequestInput!
    ) {
    phoneOtpRequest(
        input: $input
    ) {
      errors {
        path
        message
      }
      message
    }
  }
`;

export interface WithPhoneOtpRequest {
  phoneOtpRequest: (
    values: PhoneOtpRequestMutationMutationVariables
  ) => Promise<NormalizedErrorMap | null>;
  loading: boolean;
  error?: Error;
  message?: string;
}

interface Props {
  children: (data: WithPhoneOtpRequest) => ReactElement | null;
}

export const PhoneOtpRequestController: React.FC<Props> = ({ children }) => {
  const [mutate, { loading, error, data }] = useMutation<
    PhoneOtpRequestMutationMutation,
    PhoneOtpRequestMutationMutationVariables
  >(phoneOtpRequestMutation);

  const submit = async (
    values: PhoneOtpRequestMutationMutationVariables
  ): Promise<NormalizedErrorMap | null> => {
    try {
      const response = await mutate({ variables: values });

      if (!response || !response.data) {
        return { general: "An unknown error occurred" };
      }

      const phoneOtpRequest = response.data.phoneOtpRequest;

      if (phoneOtpRequest?.errors) {
        const filteredErrors = phoneOtpRequest.errors.filter(
          (e: { path: string; message: string } | null): e is { path: string; message: string } => !!e
        );
        return normalizeErrors(filteredErrors);
      }

      return null;
    } catch (mutationError) {
      const apolloError = mutationError as ApolloError;
      const message =
        apolloError.graphQLErrors?.[0]?.message ||
        apolloError.message ||
        "An error occurred";

      return mapPhoneOtpRequestApolloError(message);
    }
  };

  const message = data?.phoneOtpRequest?.message || null || undefined;

  return children({
    phoneOtpRequest: submit,
    loading,
    error,
    message
  });
};